// © Microsoft Corporation. All rights reserved.

import { AadData, AccessTokenStatus, AccountInfo, getAppEnvironment } from "@msnews/core";
import { AcquireAccessTokenFailed, EdgeChromiumAPIsNotAvailable } from "@msnews/diagnostics";

import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { ExperiencesSharedStateConnector } from "@msnews/experiences-shared-state";
import { PageBase } from "@msnews/experiences-redux";
import { Telemetry } from "@msnews/telemetry-contracts";
import { getPageTimings } from "@msnews/diagnostics";
import { start } from "repl";

/**
 * chrome
 * The chrome API is not defined in Typescript
 */
declare global {
    interface Window { chrome: any; }
}

/** The aadToken QS key */
export const aadTokenQsKey = "stn";

/** The aadToken QS value */
export const aadTokenQsVal = "aad";

// More information re: specific errors here:
// https://microsoft.visualstudio.com/Edge/_git/chromium.src?path=%2Ftools%2Fmetrics%2Fhistograms%2Fedge_enums.xml&version=GBmaster&line=3770&lineEnd=3770&lineStartColumn=1&lineEndColumn=52&lineStyle=plain
export enum TokenFetchSecondaryErrors {
    /** Token fetch operation requires user interaction */
    "kTokenFetchUserInteractionRequired" = 13,
    /** Authentication is possible, but it requires direct user interaction */
    "kInteractionRequired" = 24
}

/**
 * Gets a value to indicate if we are in an enterprise scenario
 * eg. whether we should append the AAD Token as part of the call to oneservice endpoints
 * The default value is false, so we don't need to wait until the value is explicitly set
 */
export function useEnterpriseCompliance(): boolean {
    // Obtain the current value of useEnterpriseCompliance from the ExperiencesSharedState connector
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    return sharedStateConnector ? sharedStateConnector.getCurrentState().useEnterpriseCompliance : false;
}

/**
 * Gets a value to indicate if we should use enterprise domain
 * eg. whether we should use the Falcon domain vs. consumer domain for oneservice requests
 * The default value is false, so we don't need to wait until the value is explicitly set
 */
export function useEnterpriseDomain(): boolean {
    // Obtain the current value of useEnterpriseDomain from the ExperiencesSharedState connector
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    return sharedStateConnector ? sharedStateConnector.getCurrentState().useEnterpriseDomain : false;
}

/**
 * Checks if aad token is invalid for user
 */
export function isInvalidAadToken(aadData: AadData): boolean {
    return !aadData || !aadData.isSuccess || !aadData.isValid;
}

/**
 * Fetches aad data to be used to call an authenticated API.
 * @param clientId The client id of the AAD application requesting access
 * @param scopeOrResource The scope or resource to request access for
 * @param allowInteraction Attempt to fetch the access token using the allowInteraction version of the API instead of the silent version
 */
export async function getAadData(clientId: string, scopeOrResource: string, friendlyName: string, allowInteraction = false): Promise<AadData> {
    const accountInfo = await resolveAccountInfo();

    if (!accountInfo) {
        Telemetry.sendAppErrorEvent({
            ...EdgeChromiumAPIsNotAvailable,
            message: "Auth api not available",
            pb: {
                ...EdgeChromiumAPIsNotAvailable.pb,
                customMessage: `Auth api not available for ${scopeOrResource}`
            }
        });

        return null;
    }

    return new Promise((resolve) => {
        const tokenAcquireParameters = {
            account_id: accountInfo.account_id,
            account_type: accountInfo.account_type,
            client_id: clientId,
            scope_or_resource: scopeOrResource
        };

        const totalMarkerName = `Auth.AcquireAccessToken.${friendlyName}`;
        const startMarkerName = `${totalMarkerName}.Start`;
        const endMarkerName = `${totalMarkerName}.End`;
        const pageTimings = getPageTimings();
        const startPerfMarker = pageTimings[startMarkerName];
        const endPerfMarker = pageTimings[endMarkerName];
        const totalPerfMarker = pageTimings[totalMarkerName];
        const startTime = Math.round(performance.now());
        if (!startPerfMarker && !allowInteraction) {
            pageTimings[startMarkerName] = startTime;
        }

        const tokenAccessApi = allowInteraction ? window.chrome.authPrivate.acquireAccessTokenWithUserInteraction : window.chrome.authPrivate.acquireAccessTokenSilently;

        tokenAccessApi(tokenAcquireParameters, (acquireResult) => {
            if (!endPerfMarker && !allowInteraction) {
                const endTime = Math.round(performance.now());
                pageTimings[endMarkerName] = acquireResult.is_success ? endTime : -1;
                if (!totalPerfMarker) {
                    pageTimings[totalMarkerName] = acquireResult.is_success ? endTime - startTime : -1;
                }
            }

            let status = AccessTokenStatus.success;
            if (!acquireResult.is_success) {
                const authErrorObject = {
                    ...acquireResult.error_info,
                    authPrivateApi: allowInteraction ? "acquireAccessTokenWithUserInteraction" : "acquireAccessTokenSilently",
                    accountType: tokenAcquireParameters.account_type,
                    clientId: tokenAcquireParameters.client_id,
                    resource: scopeOrResource
                };

                Telemetry.sendAppErrorEvent({
                    ...AcquireAccessTokenFailed,
                    message: "Failed to get access token",
                    pb: {
                        ...AcquireAccessTokenFailed.pb,
                        customMessage: JSON.stringify(authErrorObject)
                    }
                });

                status = acquireResult.error_info.secondary_error === TokenFetchSecondaryErrors.kTokenFetchUserInteractionRequired ||
                    acquireResult.error_info.secondary_error === TokenFetchSecondaryErrors.kInteractionRequired ?
                    AccessTokenStatus.mfaRequired : AccessTokenStatus.otherError;
            }

            resolve({
                token: acquireResult.access_token,
                errorInfo: acquireResult.error_info,
                isSuccess: acquireResult.is_success,
                isValid: acquireResult.is_token_valid,
                status
            });
        });
    });
}

/**
 * Resolve account info promise
 */
export async function resolveAccountInfo(): Promise<AccountInfo> {
    const appEnvironment = getAppEnvironment();

    if (!appEnvironment.AccountInfoPromise) {
        return null;
    }

    return await appEnvironment.AccountInfoPromise;
}